import { useContext } from 'react';

import dt from '~/testing';

import { StaticFieldDescriptorsMap } from '../StaticField/constants';
import type { StaticField } from '../StaticField/types';
import { useSectionsNavigation } from '../../context/hooks/useSectionsNavigation';
import { ContestManageContext } from '../../context/ContestManageContext';
import { useFieldValue } from '../../../create/hooks/useFieldValue';

export function StaticFieldComponent({
  field,
  sectionKey,
  FieldSwitch,
  path,
  isVisible,
  propagateFieldLabelToParent,
}: {
  field: StaticField;
  sectionKey: string;
  FieldSwitch: (props: { fieldKey: string; sectionKey: string }) => JSX.Element;
  path: string[];
  isVisible?: boolean;
  propagateFieldLabelToParent?: (path: string[], label: string | string[]) => void;
}) {
  const { values: allValues, errors: allErrors } = useContext(ContestManageContext);
  const { onGoToSection, onNextSectionClick } = useSectionsNavigation();
  const { value, onValueChange, errors, isTouched, onPreserveSections } = useFieldValue(
    sectionKey,
    path,
    'defaultValue' in field ? field.defaultValue : undefined,
    isVisible
  );
  const isHiddenField = field.type === 'HiddenField';

  const FieldDescriptor = StaticFieldDescriptorsMap[field.type];

  if (!FieldDescriptor) {
    return <div>Unknown field type: {field.type}</div>;
  }

  if (!FieldDescriptor.renderIfHidden && !isVisible) {
    return null;
  }

  if (!FieldDescriptor.FieldComponent) {
    return null;
  }

  if (isHiddenField && !FieldDescriptor.renderIfHidden) return null;

  if (FieldDescriptor.rendersNull) {
    return (
      <FieldDescriptor.FieldComponent
        field={field}
        sectionKey={sectionKey}
        value={value}
        onValueChange={onValueChange}
        onPreserveSections={onPreserveSections}
        errors={errors}
        isTouched={isTouched}
        allValues={allValues}
        allErrors={allErrors}
        FieldSwitch={FieldSwitch}
        path={path}
        onGoToSection={onGoToSection}
        onNextSectionClick={onNextSectionClick}
        isVisible={isVisible}
        propagateFieldLabelToParent={propagateFieldLabelToParent}
      />
    );
  }

  return (
    <div data-test-id={dt.contest.create.staticFieldComponent} id={path.join('-')}>
      <FieldDescriptor.FieldComponent
        field={field}
        sectionKey={sectionKey}
        value={value}
        onValueChange={onValueChange}
        onPreserveSections={onPreserveSections}
        errors={errors}
        isTouched={isTouched}
        allValues={allValues}
        allErrors={allErrors}
        FieldSwitch={FieldSwitch}
        path={path}
        onGoToSection={onGoToSection}
        onNextSectionClick={onNextSectionClick}
        isVisible={isVisible}
        propagateFieldLabelToParent={propagateFieldLabelToParent}
      />
    </div>
  );
}
